import { useInfiniteQuery } from '@tanstack/react-query';
import { fetchEventTriggeredFlowsPaginated } from '@/lib/api/email-flows';
import { QUERY_KEYS, SENTRY_FEATURES } from '@/lib/constants';
import { FlowSummary, PaginatedFlowsResponse } from '@/lib/api/email-flows/types';

const PER_PAGE = 10; // Start small for testing

/**
 * Hook to fetch event-triggered email flows with pagination
 * Uses infinite query to accumulate pages with "Show More" pattern
 * Analytics are automatically included by the backend
 */
export function useEmailFlows() {
    const query = useInfiniteQuery<PaginatedFlowsResponse, Error>({
        queryKey: [QUERY_KEYS.EMAIL_FLOWS_LIST],
        queryFn: ({ pageParam }) =>
            fetchEventTriggeredFlowsPaginated({
                page: pageParam as number,
                per_page: PER_PAGE,
            }),
        initialPageParam: 1,
        getNextPageParam: lastPage => {
            return lastPage.has_more ? lastPage.page + 1 : undefined;
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
        meta: {
            error: {
                sentryMessage: '[EMAIL_FLOWS] failed-to-fetch-event-triggered-flows',
                feature: SENTRY_FEATURES.EMAIL_FLOWS,
            },
        },
    });

    // Flatten all pages into single array
    const flows: FlowSummary[] = query.data?.pages.flatMap(page => page.flows) ?? [];
    const total = query.data?.pages[0]?.total ?? 0;

    return {
        flows,
        total,
        isLoading: query.isLoading,
        isFetchingNextPage: query.isFetchingNextPage,
        hasNextPage: query.hasNextPage ?? false,
        fetchNextPage: query.fetchNextPage,
        error: query.error,
        refetch: query.refetch,
    };
}
